import { type ChatConfigResponse } from '@myre/app-agent-generated/types/ChatConfigResponse';
import { type UiLibI18n } from '@myre/domain-i18n-wordings';
import { CopierService } from '@myre/ui-primitive-core';

import {
  Component,
  computed,
  inject,
  input,
  signal,
  ViewChild,
} from '@angular/core';

import { WORDINGS } from '../../../myre-wordings-loader';

import {
  ChatWindowService,
  type ChatWindowState,
} from '../../chat-window.service';
import { ChatComponent } from '../../chat.component';
import { type ChatActivityStatus } from '../../interfaces/chat.interfaces';

@Component({
  selector: 'myre-chat-window',
  standalone: true,
  imports: [ChatComponent],
  templateUrl: './chat-window.component.html',
  styleUrl: './chat-window.component.scss',
})
export class ChatWindowComponent {
  private readonly _chatWindowService = inject(ChatWindowService);
  private readonly _copierService = inject(CopierService);
  private readonly _wordings = inject<UiLibI18n>(WORDINGS);

  protected get chatWordings() {
    return this._wordings._library.chat;
  }

  @ViewChild(ChatComponent)
  private readonly _chatComponent?: ChatComponent;

  public readonly windowState = input.required<ChatWindowState>();
  public readonly apiConfig = input<ChatConfigResponse>();
  public readonly welcomeIconUrl = input<string>();
  public readonly welcomeIcon = input<string>();

  protected readonly _isOpen = computed(
    () => this.windowState().state === 'open',
  );

  protected readonly _isExpanded = this._chatWindowService.expanded;
  protected readonly _showMenu = signal(false);

  protected readonly _hasMessages = computed(
    () => this.windowState().hasMessages,
  );

  protected _onMinimize(): void {
    this._chatWindowService.minimizeWindow(this.windowState().id);
  }

  protected _onToggleExpand(): void {
    this._chatWindowService.toggleExpand();
  }

  protected _onClose(): void {
    this._chatWindowService.closeWindow(this.windowState().id);
  }

  protected _onTitleChanged(title: string): void {
    this._chatWindowService.updateTitle(this.windowState().id, title);
  }

  protected _onStatusChanged(status: ChatActivityStatus): void {
    this._chatWindowService.setStatus(this.windowState().id, status);
  }

  protected _toggleMenu(): void {
    this._showMenu.update(v => !v);
  }

  protected _closeMenu(): void {
    this._showMenu.set(false);
  }

  protected _onCopyMarkdown(): void {
    this._showMenu.set(false);

    const markdown = this._chatComponent?.getMessagesAsMarkdown() ?? '';

    this._copierService.copyText(markdown);
  }

  protected _onDelete(): void {
    this._showMenu.set(false);
    this._chatComponent?.deleteCurrentSession();
    this._chatWindowService.closeWindow(this.windowState().id);
  }
}
